// 公開画面の日本語／英語の切り替え（planning/2026-10-13-英語対応.md）。
// 既定は日本語で、英語は「English」を押したときと、?lang=en 付きのリンク（英語のメール・招待）から来たときだけにする（ブラウザの言語では決めない）。
// 画面の文はコードの中で L("日本語", "English") と書き、日本語のときは今までの日本語の文字列をそのまま返す
export type Lang = "ja" | "en";
const KEY = "trunkip-lang";

export function detectLang(search: string, stored: string | null): Lang {
  const q = new URLSearchParams(search).get("lang");
  if (q === "en" || q === "ja") return q;
  return stored === "en" ? "en" : "ja";
}
const readStored = (): string | null => { try { return typeof localStorage === "undefined" ? null : localStorage.getItem(KEY); } catch { return null; } };
const writeStored = (l: Lang) => { try { localStorage.setItem(KEY, l); } catch { /* 保存できない端末では URL だけで引き継ぐ */ } };

// 言語は公開ページの initLang() で決める。管理画面・主催者ページは initLang() を呼ばないため、端末に英語が保存されていても日本語のままである
let current: Lang = "ja";
export const lang = (): Lang => current;
export const isEn = (): boolean => current === "en";
export const L = (ja: string, en: string): string => (current === "en" ? en : ja);
// テストで言語を切り替える
export function setLang(l: Lang) { current = l; }

// サイト内の URL に、英語のときだけ lang=en を付ける
export function withLang(url: string, l: Lang = current): string {
  if (l !== "en" || !url.startsWith("/") || /[?&]lang=/.test(url)) return url;
  const [path, hash] = url.split("#");
  return `${path}${path.includes("?") ? "&" : "?"}lang=en${hash !== undefined ? `#${hash}` : ""}`;
}

const LEGAL: Record<string, string> = { "/terms/": "Terms of Use", "/privacy/": "Privacy Policy", "/tokushoho/": "Legal Notice (Specified Commercial Transactions Act)" };
// HTML の固定の文を英語にする。data-en（文字）・data-en-html（リンクを含む文）・data-en-placeholder・data-en-aria を差し替える。
// 規約のリンクは英語の名前に「(Japanese only)」を添える
export function applyStaticEn(root: ParentNode = document) {
  if (current !== "en") return;
  root.querySelectorAll<HTMLElement>("[data-en]").forEach((el) => { el.textContent = el.dataset.en!; });
  root.querySelectorAll<HTMLElement>("[data-en-html]").forEach((el) => { el.innerHTML = el.dataset.enHtml!; });
  root.querySelectorAll<HTMLInputElement>("[data-en-placeholder]").forEach((el) => { el.placeholder = el.dataset.enPlaceholder!; });
  root.querySelectorAll<HTMLElement>("[data-en-aria]").forEach((el) => { el.setAttribute("aria-label", el.dataset.enAria!); });
  root.querySelectorAll<HTMLAnchorElement>("footer a[href]").forEach((a) => { const k = a.getAttribute("href")!; if (LEGAL[k]) a.textContent = `${LEGAL[k]} (Japanese only)`; });
}

// ヘッダーに「日本語 / English」の切り替えを置き、<html lang> と固定の文を合わせ、サイト内リンクで言語を引き継ぐ
export function initLang() {
  if (typeof document === "undefined") return;
  current = detectLang(location.search, readStored());
  const q = new URLSearchParams(location.search).get("lang");
  if (q === "en" || q === "ja") writeStored(q);
  document.documentElement.lang = current;
  const head = document.querySelector("header .wrap");
  if (head && !head.querySelector(".lang-switch")) {
    const sw = document.createElement("span");
    sw.className = "lang-switch";
    sw.innerHTML = `<a href="#" data-lang="ja" ${current === "ja" ? 'aria-current="true"' : ""} lang="ja">日本語</a> / <a href="#" data-lang="en" ${current === "en" ? 'aria-current="true"' : ""} lang="en">English</a>`;
    sw.addEventListener("click", (ev) => {
      const a = (ev.target as HTMLElement).closest<HTMLAnchorElement>("a[data-lang]"); if (!a) return;
      ev.preventDefault();
      const l = a.dataset.lang as Lang; writeStored(l);
      const u = new URL(location.href); u.searchParams.set("lang", l); location.href = u.toString();
    });
    head.appendChild(sw);
  }
  applyStaticEn();
  // 英語のときは、日本語のページ名を外して「TRUNKIP」だけにする（ページの表示のあとで英語の名前を付けるページもある）
  if (current === "en" && /[ぁ-んァ-ン一-龥]/.test(document.title)) document.title = "TRUNKIP";
  // 英語のときは、押したサイト内リンクに lang=en を付ける（あとから描いたリンクにも効く）
  if (current === "en") document.addEventListener("click", (ev) => {
    const a = (ev.target as HTMLElement).closest<HTMLAnchorElement>("a[href]");
    if (a && !a.dataset.lang) { const h = a.getAttribute("href")!; if (h.startsWith("/")) a.setAttribute("href", withLang(h)); }
  }, true);
}
